import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import animateScrollTo from 'animated-scroll-to'
import ClipboardJS from 'clipboard'
import QueryString from 'qs'
import useExtractDetailsResponse from './useExtractDetailsResponse'
import conf from '~/app/conf'
import { GoToTop, MarkerType, Tabs, Toolbar, useGoToTop, Retry, styled } from '~/app/components'
import { Toast, ToastHandler } from '@yanolja-org/ya-com-toast'
import HostInformation from '../../common/HostInformation'
import CancelPolicy from '../../common/CancelPolicy'
import RoomInformation from './RoomInformation'
import DetailedInformation from './DetailedInformation'
import { AccommodationDetailsQuery } from '~/__generated__/app/query/order-details/AccommodationDetails.graphql'
import { ApolloError } from '@apollo/client'
import useToolbarScroll from '~/app/components/common/Toolbar/useToolbarScroll'
import { CategoryOrderIdContainer } from '~/app/components/order-list/details/OrderDetailStyle'
import { getHasDayUseBadge } from '~/api/utils/accommodationBadgesWithPolicy'
import RatePlanInformation from './RatePlanInformation'
import { DomesticRatePlanBenefits } from '~/__generated__/__types__'
import { isTruthy } from '~/app/utils'
import { Content, ContentContainer, Layout } from '~/app/components/layout-style'

/**
 * AccommodationInformation component
 * author: soojin.oh
 */

export interface AccommodationInfo extends AccommodationDetailsQuery {
  benefits?: DomesticRatePlanBenefits[]
  ratePlanTitle?: string
  ratePlanDescription?: string
}

const ContentWrap = styled(Content)`
  background: white;
  justify-content: flex-start;
  padding: 1rem 0 6.4rem;
  min-height: calc(100vh - 8rem);
`

// type definitions
type Props = {
  checkIn: string
  checkOut: string
  cancelPolicyId: string
  response?: AccommodationInfo | null
  onBackClick: () => void
  getAccommodationDetails: () => void
  error?: ApolloError
  categoryOrderId: string
}

// AccommodationInformation component
const AccommodationInformation: FC<Props> = function ({
  checkIn,
  checkOut,
  cancelPolicyId,
  response,
  onBackClick,
  error,
  getAccommodationDetails,
  categoryOrderId,
}) {
  const toastRef = useRef<ToastHandler>()
  const triggerToast = useCallback(() => {
    toastRef.current?.addMessage({ message: '주소를 복사했어요', timeout: 3000 })
  }, [])

  const scrollTarget = useRef<HTMLDivElement>(null)
  const { isFabOpen, onClick } = useGoToTop(scrollTarget)
  const { hasShadow } = useToolbarScroll(scrollTarget)

  const scrollToId = useCallback(id => {
    const target = document.getElementById(id)
    animateScrollTo(target!, { elementToScroll: scrollTarget.current! })
  }, [])

  const onTabChanged = useCallback(selectedTab => {
    setSelectedTab(selectedTab)
    scrollToId(selectedTab)
    window.location.replace(`${window.location.pathname}${window.location.search}#${selectedTab}`)
  }, [])

  const isDayuse = useMemo(() => {
    return getHasDayUseBadge(response?.roomType.config?.badges)
  }, [response])

  const cancelPolicyUrl = useMemo(() => {
    const queryString = QueryString.stringify({
      policyId: cancelPolicyId,
      checkinDate: checkIn,
      checkoutDate: checkOut,
      isDayuse,
      embed: true,
      page: 'ORDER_DETAIL',
    })

    if (!cancelPolicyId || !checkIn || !checkOut) {
      return ''
    }

    return `${conf.policy.url}/pf/domestic/cancel?${queryString}`
  }, [cancelPolicyId, isDayuse])

  const {
    hostName,
    address,
    phone,
    customerServicePhone,
    coordinate,
    roomName,
    capacity,
    smoking,
    notices,
    themes,
    direction,
    pickup,
    bed,
    description,
    ratePlanTitle,
    ratePlanBenefits,
  } = useExtractDetailsResponse(response) || {}

  const showRatePlanInformation = useMemo(() => {
    const hasBenefits = (ratePlanBenefits?.length || 0) > 0
    return !!ratePlanTitle || !!description || hasBenefits
  }, [ratePlanBenefits, ratePlanTitle, description])

  const tabsId = useRef({
    host: 'host',
    room: 'room',
    rateplan: 'rateplan',
    rule: 'rule',
    cancel: 'cancel',
  }).current

  const tabs = useMemo(() => {
    return [
      { label: '업체 정보', value: tabsId.host },
      { label: '객실 정보', value: tabsId.room },
      showRatePlanInformation && { label: '요금제 정보', value: tabsId.rateplan },
      { label: '이용 안내', value: tabsId.rule },
      { label: '취소 규정', value: tabsId.cancel },
    ].filter(isTruthy)
  }, [showRatePlanInformation])

  const [selectedTab, setSelectedTab] = useState(tabs[0].value)

  useEffect(() => {
    const hash = window.location.hash.replace('#', '')
    if (hash) {
      scrollToId(hash)
      setSelectedTab(hash)
    }
    const clipboard = new ClipboardJS('.link')
    clipboard.on('success', triggerToast)
  }, [])

  useEffect(() => getAccommodationDetails(), [getAccommodationDetails])

  return (
    <Layout>
      <Toolbar
        title={'이용정보'}
        type={'close'}
        onClickBackButton={onBackClick}
        hasShadow={hasShadow}
        borderless={false}
      >
        <Tabs
          tabs={tabs}
          currentTab={selectedTab}
          onTabChangeHandler={onTabChanged}
          filled={true}
          borderless={true}
        />
      </Toolbar>
      <ContentContainer>
        <ContentWrap ref={scrollTarget}>
          <Retry hasError={!!error} onRetryClick={getAccommodationDetails}>
            <CategoryOrderIdContainer>
              <div>숙소 예약번호 {categoryOrderId}</div>
            </CategoryOrderIdContainer>
            <HostInformation
              id={tabsId.host}
              hostName={hostName}
              phone={phone}
              customerServicePhone={customerServicePhone}
              address={address}
              coordinate={coordinate}
              markerType={MarkerType.NORMAL}
            />
            <RoomInformation
              id={tabsId.room}
              roomName={roomName}
              capacity={capacity}
              bed={bed}
              smoking={smoking}
            />
            {showRatePlanInformation && (
              <RatePlanInformation
                id={tabsId.rateplan}
                description={description}
                ratePlanTitle={ratePlanTitle}
                ratePlanBenefits={ratePlanBenefits}
              />
            )}
            <DetailedInformation
              id={tabsId.rule}
              notices={notices}
              direction={direction}
              pickup={pickup}
              themes={themes}
            />
            <CancelPolicy id={tabsId.cancel} cancelPolicyUrl={cancelPolicyUrl} />
          </Retry>
        </ContentWrap>
      </ContentContainer>
      <GoToTop isOpened={isFabOpen} onClick={onClick} />
      <Toast toastRef={toastRef} />
    </Layout>
  )
}

export default AccommodationInformation
